/**
 * Candidate time-zone derivation for the results filter (STN client round
 * 2026-08). Timezone is stored NOWHERE in the corpus — it is derived from the
 * candidate's recorded US state (dominant zone for states that span two), or
 * "Outside US" when the country is positively known to be non-US. Unknown
 * location ⇒ null ⇒ the candidate is NEVER excluded by a timezone selection
 * (standing honest-blanks policy, utils/location.ts).
 *
 * The state key set is asserted complete (50 + DC) by timezone.spec.ts so it
 * can't silently drift from US_STATE_CODES in utils/location.ts.
 */
import type { MapsCandidateLocation } from '../models/maps.types';
import { IsKnownNonUs, NormalizeUsState, US_STATE_CODES } from './location';

export type MapsTimezoneKey =
    | 'pacific'
    | 'mountain'
    | 'central'
    | 'eastern'
    | 'alaska'
    | 'hawaiiAleutian'
    | 'outsideUs';

/** Mockup display order (rows render only when their facet count is ≥ 1). */
export const TIMEZONE_ORDER: readonly MapsTimezoneKey[] = [
    'pacific',
    'mountain',
    'central',
    'eastern',
    'hawaiiAleutian',
    'alaska',
    'outsideUs',
];

export const TIMEZONE_LABELS: Record<MapsTimezoneKey, string> = {
    pacific: 'Pacific Time',
    mountain: 'Mountain Time',
    central: 'Central Time',
    eastern: 'Eastern Time',
    hawaiiAleutian: 'Hawaii–Aleutian Time',
    alaska: 'Alaska Time',
    outsideUs: 'Outside US',
};

/** USPS code → dominant zone. Split states take their majority zone. */
export const STATE_TIMEZONES: Record<string, MapsTimezoneKey> = {
    // Pacific
    wa: 'pacific', or: 'pacific', ca: 'pacific', nv: 'pacific',
    // Mountain
    mt: 'mountain', id: 'mountain', wy: 'mountain', ut: 'mountain',
    co: 'mountain', az: 'mountain', nm: 'mountain',
    // Central
    nd: 'central', sd: 'central', ne: 'central', ks: 'central', ok: 'central',
    tx: 'central', mn: 'central', ia: 'central', mo: 'central', ar: 'central',
    la: 'central', wi: 'central', il: 'central', ms: 'central', al: 'central',
    tn: 'central',
    // Eastern
    me: 'eastern', nh: 'eastern', vt: 'eastern', ma: 'eastern', ri: 'eastern',
    ct: 'eastern', ny: 'eastern', nj: 'eastern', pa: 'eastern', de: 'eastern',
    md: 'eastern', dc: 'eastern', va: 'eastern', wv: 'eastern', nc: 'eastern',
    sc: 'eastern', ga: 'eastern', fl: 'eastern', oh: 'eastern', mi: 'eastern',
    in: 'eastern', ky: 'eastern',
    // Non-contiguous
    ak: 'alaska',
    hi: 'hawaiiAleutian',
};

/**
 * A candidate's derived timezone: state-based when the state is known,
 * 'outsideUs' when the country is positively non-US, null otherwise
 * (null = unknown = never filtered out).
 */
export function TimezoneOf(loc: MapsCandidateLocation | null | undefined): MapsTimezoneKey | null {
    const state = loc?.state?.trim();
    if (state) {
        const zone = STATE_TIMEZONES[NormalizeUsState(state)];
        if (zone) return zone;
    }
    return IsKnownNonUs(loc) ? 'outsideUs' : null;
}

// Re-exported so the spec can assert STATE_TIMEZONES covers exactly the
// US_STATE_CODES key space without reaching into location.ts internals.
export { US_STATE_CODES };
